// Pure, locale-aware relative-time formatter (contract 04 §3.2, §14). REBUILT from the legacy
// `timeAgo` (`format.js:462–495`), which was a hand-rolled English interval table with English
// pluralisation baked in (`'just now'`, `` `${count} ${label}${count>1?'s':''} ago` ``) — it could
// not be localised by porting because the English *was* the implementation. Here it emits ZERO
// English literals: everything routes through `Intl.RelativeTimeFormat`, so `"3 days ago"` (en-US) /
// `"vor 3 Tagen"` (de-DE), and future dates render `"in 3 days"` / `"in 3 Tagen"`.
//
// PRESERVED (the one behaviour the ticket keeps, contract §14): the naive-timestamp `+'Z'` coercion —
// a server string like `2026-07-14T09:00:00` with no zone is treated as UTC before comparison, so it
// does not drift by the viewer's offset. Zero Vue, zero Pinia, zero network.

import { FALLBACK_LOCALE, SENTINEL, safeLocale } from './createFormatter'
import type { FormatRelativeOptions } from './types'

/** A `YYYY-MM-DDTHH:MM:SS…` string whose time part carries no zone marker (legacy `format.js:469`). */
const NAIVE_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

/** Largest-first unit table (seconds per unit); the largest whose count ≥ 1 wins (legacy L478–484). */
const UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31536000],
  ['month', 2592000],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
]

const cache = new Map<string, Intl.RelativeTimeFormat>()
function getRtf(locale: string, numeric: 'auto' | 'always'): Intl.RelativeTimeFormat {
  const tag = safeLocale(locale)
  const key = `${tag}|${numeric}`
  const cached = cache.get(key)
  if (cached) return cached

  let rtf: Intl.RelativeTimeFormat
  try {
    rtf = new Intl.RelativeTimeFormat(tag, { numeric })
  } catch {
    rtf = new Intl.RelativeTimeFormat(FALLBACK_LOCALE, { numeric })
  }
  cache.set(key, rtf)
  return rtf
}

function pickUnit(absSeconds: number): readonly [Intl.RelativeTimeFormatUnit, number] {
  for (const entry of UNITS) {
    if (absSeconds >= entry[1]) return entry
  }
  return ['second', 1]
}

/**
 * Localised relative time. Nullish / empty / unparseable input returns the `'-'` sentinel. A naive
 * (zoneless) ISO timestamp is coerced to UTC with `+'Z'` before comparison. The sign is handled by
 * `Intl.RelativeTimeFormat` (`numeric:'auto'`), so under a minute renders `"now"` / `"jetzt"`, the
 * past renders `"3 days ago"`, and the future renders `"in 3 days"` — all locale-driven.
 */
export function formatRelative(
  value: string | number | Date | null | undefined,
  locale: string,
  options: FormatRelativeOptions = {},
): string {
  if (value == null || value === '') return SENTINEL

  let input: string | number | Date = value
  if (typeof value === 'string' && NAIVE_ISO.test(value) && !/[Z+-]/.test(value.slice(10))) {
    input = `${value}Z`
  }

  const date = new Date(input)
  if (Number.isNaN(date.getTime())) return SENTINEL

  // Positive = future, negative = past — the sign `Intl.RelativeTimeFormat` expects.
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000)
  const [unit, unitSeconds] = pickUnit(Math.abs(diffSeconds))
  const amount =
    options.round === 'trunc'
      ? Math.trunc(diffSeconds / unitSeconds)
      : Math.round(diffSeconds / unitSeconds)
  return getRtf(locale, options.numeric ?? 'auto').format(amount, unit)
}
